import { Group } from '@gripp/shared-logic';
import {
  colors,
  GroupSelector,
  ResetIcon,
  tokens,
} from '@gripp/shared-ui';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';

type MainProps = {
  selectedGroups: Group[];
  onGroupsChange: (groups: Group[]) => void;
  onCreateGroup: (name: string) => Promise<Group>;
  onOpenPicker: () => void;
};

type HeaderActionsProps = {
  onReset: () => void;
};

/**
 * Main body of the accounts selection side panel. Renders the Crop Circle
 * `GroupSelector` for the currently selected accounts. The `+` button opens
 * a dedicated picker (see `AccountsGroupsPicker`) mounted as a thirdColumn
 * in the SlideLayout.
 */
export const AccountsSelectionPanelMain = ({
  selectedGroups,
  onGroupsChange,
  onCreateGroup,
  onOpenPicker,
}: MainProps) => {
  const { t } = useTranslation();

  return (
    <View style={styles.container}>
      <Text style={[tokens.typography.label.largeEmphasised, styles.label]}>
        {t('asset.groups')}
      </Text>
      <GroupSelector
        selectedGroups={selectedGroups}
        availableGroups={[]}
        onGroupsChange={onGroupsChange}
        onCreateGroup={onCreateGroup}
        onSelectorEdit={onOpenPicker}
      />
    </View>
  );
};

/**
 * Header action rendered in the SlideLayout panel header (top-right).
 * Clicking clears any groups applied through this panel and deselects the
 * underlying rows (handled by the parent via `onReset`).
 */
export const AccountsSelectionPanelHeaderActions = ({
  onReset,
}: HeaderActionsProps) => (
  <Pressable onPress={onReset} style={styles.headerButton}>
    <ResetIcon size={20} color={colors.brand.black} />
  </Pressable>
);

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 8,
  },
  label: {
    color: colors.brand.black,
  },
  headerButton: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
